import api from "./api";

// Types
export interface LotteryPrize {
  id: number;
  rank: number;
  name: string;
  description?: string;
  image_url?: string;
  prize_type: string;  // "point" or "product"
  point_amount?: number;
  is_active: boolean;
}

export interface LotteryPrizeCreate {
  rank: number;
  name: string;
  description?: string;
  image_url?: string;
  prize_type: string;  // "point" or "product"
  point_amount?: number;
}

export interface LotteryPrizeUpdate {
  name?: string;
  description?: string;
  image_url?: string;
  prize_type?: string;
  point_amount?: number;
  is_active?: boolean;
}

export interface LotteryTicket {
  id: number;
  issue_date: string;
  status: string; // pending, opened, expired
  is_winner: boolean;
  won_rank?: number;
  prize?: LotteryPrize;
  opened_at?: string;
  created_at: string;
  // 수령 상태 정보 (상품권 당첨의 경우)
  is_received?: boolean;
  received_at?: string;
}

export interface ScratchResult {
  success: boolean;
  is_winner: boolean;
  prize?: LotteryPrize;
  message: string;
}

export interface UserBrief {
  id: string;
  name: string;
  department_name?: string;
  profile_image?: string;
}

export interface LotteryWinner {
  id: number;
  user: UserBrief;
  rank: number;
  won_date: string;
  prize_name: string;
  prize_description?: string;
  prize_image_url?: string;
  prize_type: string;
  point_amount?: number;
  is_received: boolean;
  received_at?: string;
}

export interface MonthlyStats {
  year: number;
  month: number;
  total_issued: number;
  total_winners: number;
  remaining_ranks: number[];
  won_ranks: number[];
  win_rate: number;
}

export interface CurrentMonthStats {
  year: number;
  month: number;
  total_prizes: number;
  remaining_count: number;
  won_count: number;
  remaining_ranks: number[];
  won_ranks: Record<number, string>;  // {rank: username}
  win_probability: number;
}

export interface PersonalStats {
  total_tickets: number;  // 총 받은 복권 수
  total_wins_all_time: number;  // 전체 당첨 횟수
  total_wins_this_month: number;  // 이번 달 당첨 횟수
  win_rate: number;  // 당첨률 (%)
  best_rank?: number;  // 최고 등수
  tickets_this_month: number;  // 이번 달 받은 복권 수
  opened_this_month: number;  // 이번 달 개봉한 복권 수
  pending_tickets: number;  // 미개봉 복권 수
  expired_this_month: number;  // 이번 달 만료된 복권 수
}

export interface ConsecutiveAttendance {
  consecutive_days: number;  // 현재 연속 정상 출근 일수
  message: string;  // 안내 메시지
}

export interface LotterySettings {
  is_enabled: boolean;
  ticket_expiry_days: number;
  pre_draw_low_threshold: number;
  pre_draw_low_weight: number;
  pre_draw_high_threshold: number;
  pre_draw_high_weight: number;
  top_prize_rank_limit: number;
  top_prize_penalty_weight: number;
  top_prize_exclusion_count: number;
}

export interface DailyIssuanceSummary {
  issue_date: string;
  total_issued: number;
  winner_count: number;
  opened_count: number;
  expired_count: number;
  pending_count: number;
}

export interface IssuanceTicket {
  id: number;
  user: UserBrief;
  issue_date: string;
  status: string;
  is_winner: boolean;
  won_rank?: number;
  opened_at?: string;
}

const lotteryService = {
  // ========== Prize Management (Admin) ==========

  // 시상품 목록 조회
  getPrizes: async (activeOnly: boolean = true): Promise<LotteryPrize[]> => {
    const response = await api.get<LotteryPrize[]>("/lottery/prizes", {
      params: { active_only: activeOnly },
    });
    return response.data;
  },

  // 시상품 등록
  createPrize: async (data: LotteryPrizeCreate): Promise<LotteryPrize> => {
    const response = await api.post<LotteryPrize>("/lottery/prizes", data);
    return response.data;
  },

  // 시상품 수정
  updatePrize: async (
    id: number,
    data: LotteryPrizeUpdate
  ): Promise<LotteryPrize> => {
    const response = await api.put<LotteryPrize>(`/lottery/prizes/${id}`, data);
    return response.data;
  },

  // 시상품 삭제
  deletePrize: async (id: number): Promise<{ success: boolean; message: string }> => {
    const response = await api.delete<{ success: boolean; message: string }>(
      `/lottery/prizes/${id}`
    );
    return response.data;
  },

  // ========== Lottery Tickets (User) ==========

  // 미개봉 복권 수 조회 (헤더 뱃지용)
  getMyPendingCount: async (): Promise<{ count: number }> => {
    const response = await api.get<{ count: number }>("/lottery/my-pending-count");
    return response.data;
  },

  // 내 복권 목록 조회
  getMyTickets: async (limit: number = 30): Promise<LotteryTicket[]> => {
    const response = await api.get<LotteryTicket[]>("/lottery/my-tickets", {
      params: { limit },
    });
    return response.data;
  },

  // 복권 긁기
  scratchTicket: async (ticketId: number): Promise<ScratchResult> => {
    const response = await api.post<ScratchResult>(
      `/lottery/scratch/${ticketId}`
    );
    return response.data;
  },

  // ========== Winners (Public) ==========

  // 당첨자 목록 조회
  getWinners: async (
    year?: number,
    month?: number,
    limit: number = 30
  ): Promise<LotteryWinner[]> => {
    const response = await api.get<LotteryWinner[]>("/lottery/winners", {
      params: { year, month, limit },
    });
    return response.data;
  },

  // ========== Admin Tools ==========

  // 사용자 자격 확인 (관리자)
  checkEligibility: async (
    userId: string,
    targetDate?: string
  ): Promise<{ user_id: string; target_date: string; is_eligible: boolean }> => {
    const response = await api.get(
      `/lottery/admin/check-eligibility/${userId}`,
      {
        params: { target_date: targetDate },
      }
    );
    return response.data;
  },

  // ========== Statistics ==========

  // 월별 통계 조회
  getMonthlyStats: async (year?: number, month?: number): Promise<MonthlyStats> => {
    const response = await api.get<MonthlyStats>("/lottery/stats/monthly", {
      params: { year, month },
    });
    return response.data;
  },

  // 이번 달 현황 조회 (당첨 확률 포함)
  getCurrentMonthStats: async (): Promise<CurrentMonthStats> => {
    const response = await api.get<CurrentMonthStats>("/lottery/stats/current-month");
    return response.data;
  },

  // ========== Personal Statistics ==========

  // 내 복권 통계 조회
  getMyStats: async (): Promise<PersonalStats> => {
    const response = await api.get<PersonalStats>("/lottery/my-stats");
    return response.data;
  },

  // 연속 출근 일수 조회
  getConsecutiveAttendance: async (): Promise<ConsecutiveAttendance> => {
    const response = await api.get<ConsecutiveAttendance>("/lottery/consecutive-attendance");
    return response.data;
  },

  // ========== Admin Functions ==========

  // 상품 수령 처리 (관리자)
  markPrizeReceived: async (winnerId: number): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(`/lottery/admin/winners/${winnerId}/mark-received`);
    return response.data;
  },

  // ========== Lottery Settings (Admin) ==========

  // 복권 설정 조회
  getLotterySettings: async (): Promise<LotterySettings> => {
    const response = await api.get<LotterySettings>("/lottery/admin/settings");
    return response.data;
  },

  // 복권 설정 업데이트
  updateLotterySettings: async (data: Partial<LotterySettings>): Promise<LotterySettings> => {
    const response = await api.put<LotterySettings>("/lottery/admin/settings", data);
    return response.data;
  },

  // ========== Issuance History (Admin) ==========

  // 일별 발급 내역 요약
  getIssuanceHistory: async (year?: number, month?: number): Promise<DailyIssuanceSummary[]> => {
    const response = await api.get<DailyIssuanceSummary[]>("/lottery/admin/issuance-history", {
      params: { year, month },
    });
    return response.data;
  },

  // 특정 날짜 발급 상세
  getIssuanceDetail: async (date: string): Promise<IssuanceTicket[]> => {
    const response = await api.get<IssuanceTicket[]>(`/lottery/admin/issuance-history/${date}`);
    return response.data;
  },
};

export default lotteryService;
